import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'support/helpers/currency/currency';
import { Summary } from '../types';
import { useDocumentCurrency } from '../../helpers';
import { countDecimalPlaces } from 'support/helpers/numbers/numbers';
import { RoundedCurrencyTooltip } from '../RoundedCurrency';
import { DocumentTypeWithVirtual } from 'support/types';

type SummaryValues = {
  subtotal: number;
  charges: number;
  allowances: number;
  taxes: number;
  due: number;
  net: number;
};

// We are calculating a more user-friendly summary for the invoice here:
// Basically we are reporting charges and allowances separately from the subtotal
// regardless of whether they are on line- or document level.
// Typically the subtotal would already have charges and allowances from the line-level included.
const calculateSummary = (initialSummary: SummaryValues, lineItems?: Array<any>): SummaryValues =>
  lineItems?.reduce(
    (acc, item) => ({
      ...acc,
      subtotal: acc.subtotal + item.line_gross_amount,
      charges: acc.charges + item.line_charge_amount,
      allowances: acc.allowances + item.line_allowance_amount,
    }),
    initialSummary,
  ) ?? initialSummary;

const useSummaryValues = (): SummaryValues => {
  const [lineItems, due, net, taxes, charges, allowances] = useWatch({
    name: [
      'line_items',
      'info.0.due_amount',
      'info.0.net_amount',
      'tax.total_tax_amount',
      'info.0.charge_amount',
      'info.0.allowance_amount',
    ],
  });

  return useMemo(
    () =>
      calculateSummary(
        {
          subtotal: 0,
          charges: charges ?? 0,
          allowances: allowances ?? 0,
          taxes: taxes ?? 0,
          due: due ?? 0,
          net: net ?? 0,
        },
        lineItems,
      ),
    [charges, allowances, taxes, due, net, lineItems],
  );
};

const useDocumentSummary = (
  documentType:
    | DocumentTypeWithVirtual.invoice
    | DocumentTypeWithVirtual.serviceInvoice
    | DocumentTypeWithVirtual.creditNote
    | DocumentTypeWithVirtual.debitNote,
): Summary => {
  const { t } = useTranslation();
  const currency = useDocumentCurrency();
  const values = useSummaryValues();

  return useMemo(() => {
    let type;
    switch (documentType) {
      case DocumentTypeWithVirtual.invoice:
      case DocumentTypeWithVirtual.serviceInvoice:
        type = 'invoice';
        break;
      case DocumentTypeWithVirtual.creditNote:
      case DocumentTypeWithVirtual.debitNote:
        type = 'creditNote'; // both have the same structure, we keep the same translations
        break;
    }
    return {
      content: [
        [
          {
            label: t(`webedi:documentSummary.${type}.grossAmount.label`),
            value: <span className="tabular-nums">{formatCurrency(values.subtotal, currency)}</span>,
          },
          {
            label: t(`webedi:documentSummary.${type}.allowances.label`),
            value: <span className="tabular-nums">- {formatCurrency(values.allowances, currency)}</span>,
          },
          {
            label: t(`webedi:documentSummary.${type}.charges.label`),
            value: <span className="tabular-nums">+ {formatCurrency(values.charges, currency)}</span>,
          },
        ],
        [
          {
            label: t(`webedi:documentSummary.${type}.net.label`),
            value: <span className="tabular-nums">{formatCurrency(values.net, currency)}</span>,
          },
          {
            label: t(`webedi:documentSummary.${type}.taxes.label`),
            value: <span className="tabular-nums">+ {formatCurrency(values.taxes, currency)}</span>,
          },
        ],
      ],
      footer: {
        label: t(`webedi:documentSummary.${type}.footer.label`),
        value:
          countDecimalPlaces(values.due) > 2 ? (
            <RoundedCurrencyTooltip value={values.due} currency={currency} type={type} />
          ) : (
            <span className="tabular-nums">
              {formatCurrency(values.due, currency, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          ),
      },
    };
  }, [t, values, currency, documentType]);
};

export const useInvoiceSummary = (): Summary => {
  return useDocumentSummary(DocumentTypeWithVirtual.invoice);
};

export const useInvoiceSummaryValues = (): SummaryValues => {
  return useSummaryValues();
};

export const useServiceInvoiceSummary = (): Summary => {
  return useDocumentSummary(DocumentTypeWithVirtual.serviceInvoice);
};

export const useCreditNoteSummaryValues = (): SummaryValues => {
  return useSummaryValues();
};

export const useCreditNoteSummary = (): Summary => {
  return useDocumentSummary(DocumentTypeWithVirtual.creditNote);
};

export const useDebitNoteSummary = (): Summary => {
  return useDocumentSummary(DocumentTypeWithVirtual.debitNote);
};

/**
 * i18n
 * t("webedi:documentSummary.invoice.grossAmount.label")
 * t("webedi:documentSummary.invoice.net.label")
 * t("webedi:documentSummary.invoice.allowances.label")
 * t("webedi:documentSummary.invoice.charges.label")
 * t("webedi:documentSummary.invoice.taxes.label")
 * t("webedi:documentSummary.invoice.footer.label")
 * t("webedi:documentSummary.invoice.footer.tooltip")
 * t("webedi:documentSummary.creditNote.grossAmount.label")
 * t("webedi:documentSummary.creditNote.net.label")
 * t("webedi:documentSummary.creditNote.allowances.label")
 * t("webedi:documentSummary.creditNote.charges.label")
 * t("webedi:documentSummary.creditNote.taxes.label")
 * t("webedi:documentSummary.creditNote.footer.label")
 * t("webedi:documentSummary.creditNote.footer.tooltip")
 */
